<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            La fotografía inmobiliaria 360° ha pasado de ser un servicio de lujo
            a una expectativa estándar en los mercados de propiedades
            competitivos. Los agentes que la dominan ganan más mandatos, venden
            más rápido y negocian mejores comisiones. Esta guía completa cubre
            el equipo, las técnicas de captura, la estrategia habitación por
            habitación y los errores más comunes.
          </p>
        </section>

        <section>
          <h2>
            Por qué la foto 360° es ahora imprescindible en el sector
            inmobiliario
          </h2>
          <p>
            El <strong>97% de los compradores</strong> comienza su búsqueda de
            propiedades en línea. De ellos, el
            <strong>74% declara que no visitaría una propiedad</strong> sin
            haber visto primero un tour virtual o fotos 360°. Las propiedades
            con contenido 360° reciben
            <strong>403% más llamadas de consulta</strong> que los anuncios solo
            con fotos. En un mercado donde los compradores preseleccionan
            propiedades digitalmente antes de comprometerse con una visita
            física, la foto 360° ya no es un diferenciador — es la expectativa
            básica.
          </p>
        </section>

        <section>
          <h2>Equipo para fotografía inmobiliaria 360°</h2>
          <h3>Smartphone + app de IA (punto de partida recomendado)</h3>
          <p>
            Un smartphone moderno combinado con una app como Travvir es el punto
            de entrada más accesible.
            <strong>Coste total: 0 a 60 €/año.</strong>
            Los teléfonos de gama alta (iPhone 14 Pro+, Pixel 8 Pro, Galaxy S24
            Ultra) capturan suficiente detalle para tours inmobiliarios
            convincentes. El ensamblaje con IA ha alcanzado un nivel donde la
            <strong
              >mayoría de los compradores no distingue el resultado del hardware
              dedicado</strong
            >
            en condiciones de visualización estándar.
          </p>
          <h3>Cámaras 360° dedicadas</h3>
          <p>
            <strong>Ricoh Theta Z1 (999 €)</strong>: la más popular entre los
            fotógrafos inmobiliarios profesionales.
            <strong>Insta360 X3 (449 €)</strong>: excelente cámara de entrada
            con buenas capacidades de vídeo.
            <strong>Matterport Pro3 (5.995 €)</strong>: el estándar profesional
            para inmobiliario de lujo que requiere escaneado espacial 3D.
          </p>
        </section>

        <section>
          <h2>Antes de la captura: preparación de la propiedad</h2>
          <p>
            Retira los objetos personales, despeja las encimeras y limpia a
            fondo (los espejos y superficies de cristal revelan todos los
            defectos en foto 360°). Enciende todas las luces, abre todas las
            persianas al mismo ángulo (unos 45°). Retira los vehículos del
            camino de entrada para las tomas exteriores.
          </p>
        </section>

        <section>
          <h2>Técnica de captura habitación por habitación</h2>
          <p>
            Colócate en el centro geométrico de cada habitación a
            aproximadamente 1,5 m de altura — la perspectiva visual natural.
            Esta posición maximiza el espacio de suelo visible y asegura una
            cobertura uniforme en todas las direcciones. Captura al menos una
            vista desde el umbral de cada puerta para conseguir transiciones
            naturales entre habitaciones.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/recorrido-virtual-inmobiliario">Cómo crear un recorrido virtual inmobiliario con tu smartphone en 2026</a></li>
          <li><a href="/blog/es/fotografia-interior-360-tour-virtual">Fotografía de Interiores para Tours Virtuales 360°: Guía Técnica</a></li>
          <li><a href="/blog/es/camara-360-vs-reflex-inmobiliaria">Cámara 360° vs Reflex/Mirrorless para Inmobiliaria: Comparativa 2026</a></li>
          <li><a href="/blog/es/tour-virtual-inmobiliario-smartphone">Tour Virtual Inmobiliario con Smartphone: Guía Completa 2026</a></li>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "guia-fotografia-360-inmobiliaria",
);

useBlogSeo({
  title: "Guía Completa de Fotografía 360° Inmobiliaria 2026 | Travvir",
  description:
    "Guía completa de fotografía 360° para el sector inmobiliario: equipo, técnicas de captura, preparación de la propiedad y flujo de trabajo profesional para agentes.",
  keywords:
    "fotografía 360 inmobiliaria, foto 360 propiedad, guía fotografía 360 agente inmobiliario",
  slug: "guia-fotografia-360-inmobiliaria",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question:
        "¿Se necesita una cámara 360° dedicada para la fotografía inmobiliaria?",
      answer:
        "No — un smartphone moderno con una app de IA como Travvir produce resultados de calidad profesional indistinguibles de una cámara dedicada en condiciones de visualización estándar.",
    },
    {
      question: "¿Cuántas capturas se necesitan por habitación?",
      answer:
        "Generalmente, una captura central por habitación es suficiente, complementada con una vista desde el umbral de la puerta para las transiciones. Las habitaciones grandes pueden necesitar 2 puntos de captura.",
    },
  ],
});

</script>
